/**
 * Iconos de la barra lateral.
 *
 * Van incrustados y no como fichero: son trazos de pocos bytes, se colorean
 * solos con `currentColor` —así siguen al estado activo y al modo oscuro sin
 * una sola línea extra— y evitan una petición por icono.
 *
 * Todos comparten rejilla de 24 y el mismo grosor de trazo. Un icono con
 * otro peso visual llama la atención sin querer decir nada, y en una barra
 * que se mira ocho horas al día eso cansa.
 *
 * **Nunca van solos**: acompañan a la etiqueta de texto, no la sustituyen.
 * Un icono sin texto obliga a aprenderse un idioma nuevo, y ERP-UX.md ya
 * fija que los estados no se distinguen sólo por color ni por forma.
 */

import type { ReactNode } from 'react';

const PATHS: Record<string, ReactNode> = {
  // Casa
  inicio: <path d="M3 10.5 12 3l9 7.5M5.5 9.5V20h13V9.5M9.5 20v-6h5v6" />,
  // Personas
  clientes: (
    <>
      <circle cx="9" cy="8" r="3" />
      <path d="M3.5 20a5.5 5.5 0 0 1 11 0M16 5.5a3 3 0 0 1 0 5.8M17 20a5.5 5.5 0 0 0-2.4-4.5" />
    </>
  ),
  // Extintor
  activos: (
    <>
      <path d="M9 8h5v13H9zM11 8V5.5h3M14 5.5h2.5" />
      <path d="M9 11h5" />
    </>
  ),
  // Calendario con marca
  mantenimiento: (
    <>
      <rect x="3.5" y="5" width="17" height="16" rx="2" />
      <path d="M3.5 10h17M8 3v4M16 3v4M9.5 15l2 2 3.5-4" />
    </>
  ),
  // Llave inglesa
  campo: (
    <path d="M15.5 3.5a5 5 0 0 0-4.4 7.4L3.5 18.5 5.5 20.5l7.6-7.6a5 5 0 0 0 6.4-6.5l-3 3-2.4-2.4 3-3a5 5 0 0 0-1.6-.5Z" />
  ),
  // Destornillador y llave cruzados
  taller: (
    <>
      <path d="M4 20 14 10M13 5l6 6M11 7l6 6" />
      <circle cx="7" cy="17" r="2" />
    </>
  ),
  // Caja
  almacen: (
    <>
      <path d="M3.5 7.5 12 3l8.5 4.5V16L12 20.5 3.5 16Z" />
      <path d="M3.5 7.5 12 12l8.5-4.5M12 12v8.5" />
    </>
  ),
  // Documento con líneas
  ventas: (
    <>
      <path d="M6 3h8l4 4v14H6Z" />
      <path d="M14 3v4h4M9 12h6M9 16h6" />
    </>
  ),
  // Carro
  compras: (
    <>
      <path d="M3 4h2.5l2.5 11h9l2.5-8H7" />
      <circle cx="9" cy="19" r="1.5" />
      <circle cx="17" cy="19" r="1.5" />
    </>
  ),
  // Recibo
  facturacion: (
    <>
      <path d="M5.5 3h13v18l-2.2-1.5-2.2 1.5-2.1-1.5L9.9 21l-2.2-1.5L5.5 21Z" />
      <path d="M9 8h6M9 12h6" />
    </>
  ),
  // Diana
  crm: (
    <>
      <circle cx="12" cy="12" r="8" />
      <circle cx="12" cy="12" r="3.5" />
      <path d="M12 2v3M12 19v3M2 12h3M19 12h3" />
    </>
  ),
  // Barras
  informes: (
    <>
      <path d="M3.5 20.5h17" />
      <path d="M7 20.5v-7M12 20.5V6M17 20.5v-10" />
    </>
  ),
  // Engranaje
  configuracion: (
    <>
      <circle cx="12" cy="12" r="3" />
      <path d="M12 2.5v2.6M12 18.9v2.6M4.2 7l2.2 1.3M17.6 15.7l2.2 1.3M4.2 17l2.2-1.3M17.6 8.3l2.2-1.3" />
    </>
  ),
};

export function NavIcon({ groupId }: { groupId: string }): ReactNode {
  const path = PATHS[groupId];
  if (!path) return null;

  return (
    <svg
      className="sidebar__icon"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      // Decorativo: la etiqueta de texto que va al lado ya dice qué es, y
      // repetirlo haría que un lector de pantalla lo leyera dos veces.
      aria-hidden="true"
      focusable="false"
    >
      {path}
    </svg>
  );
}
